import React, { useRef, useEffect } from "react";
import { LinkServiceButton } from "./ui/button";

// Импортируем иконки категорий
import proxyIcon from '../assets/icons/categories/proxy.svg';
import personalDataIcon from '../assets/icons/categories/personal-data.svg';
import callIcon from '../assets/icons/categories/call.svg';
import incognitoIcon from '../assets/icons/categories/incognito.svg';
import documentsIcon from '../assets/icons/categories/documents.svg';
import bankCardsIcon from '../assets/icons/categories/bank-cards.svg';
import cubeIcon from '../assets/icons/categories/cube.svg';
import userVerifiedIcon from '../assets/icons/categories/user-verified.svg';
import searchIcon from '../assets/icons/categories/search.svg';
import bankIcon from '../assets/icons/categories/bank.svg';
import chatIcon from '../assets/icons/categories/chat.svg';

const categoryIcons = {
    'proxy': proxyIcon,
    'personal-data': personalDataIcon,
    'call': callIcon,
    'incognito': incognitoIcon,
    'documents': documentsIcon,
    'bank-cards': bankCardsIcon,
    'cube': cubeIcon,
    'user-verified': userVerifiedIcon,
    'search': searchIcon,
    'bank': bankIcon,
    'chats': chatIcon,
};

export default function ServiceList({ servicesList = [] }) {
    const containerRef = useRef(null);

    // Scroll-linked сжатие разбега строк: по мере прохода блока вверх
    // отступы строк ужимаются (16 → 8), каскадом сверху вниз. Текст не трогаем.
    useEffect(() => {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        const START = 16, MIN_PAD = 8, EASE = 1.2;
        const CARD_START = 28, CARD_MIN = 12;
        const SMOOTH = 0.1;                   // инерция: больше = быстрее догоняет
        const CENTER = 0.4, RANGE = 0.2;     // строка ужимается, проходя через центр экрана
        const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
        // мягкий overshoot — пружинка "прынь"
        const easeOutBack = (t) => { const c1 = 1.4, c3 = c1 + 1; return 1 + c3 * (t - 1) ** 3 + c1 * (t - 1) ** 2; };

        const container = containerRef.current;
        if (!container) return;
        const blocks = Array.from(container.querySelectorAll('.svc-block'));
        const rows = Array.from(container.querySelectorAll('.svc-row'));
        // минимальный padding строки зависит от числа строк в её блоке (одиночные ужимаем сильнее);
        // у featured-строк пол выше высоты бейджа (12px), чтобы паддинг сверху/снизу оставался
        // одинаковым и контент не съезжал вниз относительно рамки
        const FEATURED_MIN_PAD = 14;
        const minPadOf = new Map();
        blocks.forEach((b) => {
            const rs = b.querySelectorAll('.svc-row');
            const mp = rs.length <= 1 ? 2 : rs.length === 2 ? 5 : MIN_PAD;
            rs.forEach((r) => minPadOf.set(r, r.classList.contains('svc-featured-row') ? FEATURED_MIN_PAD : mp));
        });

        const dispB = new Map(), dispR = new Map();
        let rafId = null;

        const cards = blocks.map((b) => b.querySelector('.svc-card'));

        const tick = () => {
            const vh = window.innerHeight;
            let moving = false;

            // ФАЗА ЧТЕНИЯ: снимаем все rect разом, до любых записей. Иначе запись padding
            // инвалидирует лейаут и каждое следующее чтение rect форсит синхронный reflow
            // (layout thrashing) — на быстром скролле это и даёт рывки.
            const blockRects = blocks.map((b) => b.getBoundingClientRect());
            const rowRects = rows.map((r) => r.getBoundingClientRect());

            // ФАЗА ЗАПИСИ: рамки блоков — по позиции блока
            blocks.forEach((block, i) => {
                const rect = blockRects[i];
                const target = Math.pow(clamp((vh - rect.top) / vh, 0, 1), EASE);
                let cur = dispB.has(block) ? dispB.get(block) : target;
                cur += (target - cur) * SMOOTH;
                if (Math.abs(target - cur) > 0.0005) moving = true; else cur = target;
                dispB.set(block, cur);
                const card = cards[i];
                if (card) {
                    const cp = (CARD_START - (CARD_START - CARD_MIN) * cur) + 'px';
                    card.style.paddingTop = cp;
                    card.style.paddingBottom = cp;
                }
            });

            // строки — единый каскад сверху вниз по собственной позиции: каждая строка
            // (в т.ч. одиночный блок) ужимается по очереди, проходя полосу у верха экрана
            rows.forEach((row, i) => {
                const rect = rowRects[i];
                // полоса сжатия центрирована на середине экрана: строка ужимается,
                // когда её верх проходит через центр (от центра+RANGE/2 до центра−RANGE/2)
                const target = clamp((vh * (CENTER + RANGE / 2) - rect.top) / (vh * RANGE), 0, 1);
                let cur = dispR.has(row) ? dispR.get(row) : target;
                cur += (target - cur) * SMOOTH;
                if (Math.abs(target - cur) > 0.0005) moving = true; else cur = target;
                dispR.set(row, cur);
                const t = easeOutBack(cur);
                const minPad = minPadOf.get(row) ?? MIN_PAD;
                const pad = Math.max(1, START - (START - minPad) * t) + 'px';
                row.style.paddingTop = pad;
                row.style.paddingBottom = pad;
            });

            rafId = moving ? requestAnimationFrame(tick) : null;
        };

        const kick = () => { if (rafId == null) rafId = requestAnimationFrame(tick); };

        window.addEventListener('scroll', kick, { passive: true });
        window.addEventListener('resize', kick, { passive: true });
        kick();
        return () => {
            window.removeEventListener('scroll', kick);
            window.removeEventListener('resize', kick);
            if (rafId != null) cancelAnimationFrame(rafId);
        };
    }, [servicesList]);

    return (
        <div className="w-full flex justify-center mt-12 px-4 md:px-6 min-h-[30rem]">
            <div ref={containerRef} className="w-full max-w-[1140px] flex flex-col items-center">
                {servicesList?.length > 0 &&
                    servicesList
                        .filter(({ services = [] }) => services.length > 0)
                        .map(({ name, icon, services = [] }, categoryIndex) => {
                            return (
                                <div key={categoryIndex} className="svc-block w-full mb-8 relative">
                                    {/* Services List Container */}
                                    <div
                                        className="svc-card w-full rounded-xl flex flex-col relative"
                                        style={{
                                            border: '1px solid #FF00A7',
                                            padding: '28px 20px',
                                        }}
                                    >
                                        {/* Category Heading - вписан в верхнюю границу */}
                                        <div className="absolute -top-[18px] left-1/2 transform -translate-x-1/2">
                                            <div
                                                className="inline-flex items-center gap-2 px-4 sm:px-8 h-9 rounded-[28px] font-manrope font-bold text-sm sm:text-base leading-[1.25em] text-white whitespace-nowrap"
                                                style={{
                                                    backgroundColor: '#FF00A7',
                                                }}
                                            >
                                                {icon && categoryIcons[icon] && (
                                                    <img src={categoryIcons[icon]} alt="" className="w-4 h-4" />
                                                )}
                                                <span>{name}</span>
                                            </div>
                                        </div>
                                        {/* Featured — это бейдж поверх обычной ротации, а не отдельная
                                            зона сверху: карточка остаётся на своём месте в обычном или
                                            закреплённом порядке (см. backend/ordering.py) */}
                                        {services.map((service, serviceIndex) => {
                                            const isLast = serviceIndex === services.length - 1;
                                            const nextIsFeatured = !isLast && services[serviceIndex + 1].is_featured;

                                            if (service.is_featured) {
                                                return (
                                                    <div
                                                        key={service.id}
                                                        className="svc-row svc-featured-row rounded-xl flex flex-col relative px-2.5 -mx-2.5"
                                                        style={{
                                                            border: '1px solid #FF00A7',
                                                            boxShadow:
                                                                '0 0 32px rgba(255, 0, 167, 0.7), inset 0 0 10px rgba(255, 0, 167, 0.65), inset 0 0 45px rgba(255, 0, 167, 0.4)',
                                                            paddingTop: '16px',
                                                            paddingBottom: '16px',
                                                            marginTop: serviceIndex === 0 ? '26px' : '20px',
                                                            marginBottom: isLast ? '0' : '16px',
                                                        }}
                                                    >
                                                        <div
                                                            className="absolute -top-3 left-4 inline-flex items-center gap-1 px-3 h-6 rounded-full text-xs font-manrope font-bold text-[#FF00A7] bg-[#0F0D15]"
                                                            style={{ border: '1px solid #FF00A7' }}
                                                        >
                                                            ⭐ Featured Partner
                                                        </div>
                                                        <div className="flex flex-row items-center gap-4 w-full">
                                                            <div className="flex flex-col gap-1 flex-1">
                                                                {(() => {
                                                                    const parts = service.name.split('-');
                                                                    const boldPart = parts[0]?.trim();
                                                                    const restPart = parts.slice(1).join('-').trim();

                                                                    return (
                                                                        <>
                                                                            <h3 className="text-white font-manrope font-bold text-base leading-[1.25em]">
                                                                                {boldPart}
                                                                            </h3>
                                                                            {restPart && (
                                                                                <p className="text-[#D5CED4]/70 font-manrope font-normal text-sm leading-[1.4285714285714286em]">
                                                                                    {restPart}
                                                                                </p>
                                                                            )}
                                                                        </>
                                                                    );
                                                                })()}
                                                                {service.description && (
                                                                    <p className="text-[#D5CED4]/70 font-manrope font-normal text-sm leading-[1.4285714285714286em]">
                                                                        {service.description}
                                                                    </p>
                                                                )}
                                                            </div>
                                                            <a
                                                                href={service.link}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="flex-shrink-0"
                                                                onClick={() => navigator.sendBeacon(`/api/click/service/${service.id}`)}
                                                            >
                                                                <LinkServiceButton variant="featured" />
                                                            </a>
                                                        </div>
                                                    </div>
                                                );
                                            }

                                            return (
                                                <div
                                                    key={service.id}
                                                    className="svc-row flex flex-row items-center gap-4 w-full"
                                                    style={{
                                                        paddingTop: '16px',
                                                        paddingBottom: '16px',
                                                        borderBottom:
                                                            !isLast && !nextIsFeatured
                                                                ? '1px solid rgba(255, 0, 167, 0.5)'
                                                                : 'none',
                                                    }}
                                                >
                                                    {/* Service Name and Description */}
                                                    <div className="flex flex-col gap-1 flex-1">
                                                        {(() => {
                                                            const parts = service.name.split('-');
                                                            const boldPart = parts[0]?.trim();
                                                            const restPart = parts.slice(1).join('-').trim();

                                                            return (
                                                                <>
                                                                    <h3 className="text-white font-manrope font-bold text-base leading-[1.25em]">
                                                                        {boldPart}
                                                                    </h3>
                                                                    {restPart && (
                                                                        <p className="text-[#D5CED4]/70 font-manrope font-normal text-sm leading-[1.4285714285714286em]">
                                                                            {restPart}
                                                                        </p>
                                                                    )}
                                                                </>
                                                            );
                                                        })()}
                                                        {service.description && (
                                                            <p className="text-[#D5CED4]/70 font-manrope font-normal text-sm leading-[1.4285714285714286em]">
                                                                {service.description}
                                                            </p>
                                                        )}
                                                    </div>

                                                    {/* Link Button */}
                                                    <a
                                                        href={service.link}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="flex-shrink-0"
                                                        onClick={() => navigator.sendBeacon(`/api/click/service/${service.id}`)}
                                                    >
                                                        <LinkServiceButton />
                                                    </a>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
            </div>
        </div>
    );
}
